import type { TOptions } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLocalStorage } from "../hooks/useLocalStorage";
import type { Translations } from "./interfaces/translationTypes";

export const useTranslations = <N extends keyof Translations>(namespace?: N) => {
  const { t, i18n } = useTranslation(namespace);
  const { setLocalStorage, getLocalStorage } = useLocalStorage();

  const [activeLanguage, setActiveLanguage] = useState(() => {
    return i18n.language;
  });

  const setLanguage = useCallback(
    async (lang: string) => {
      await i18n.changeLanguage(lang);
      setLocalStorage({
        key: "language",
        value: lang,
      });
      setActiveLanguage(lang);
    },
    [i18n, setLocalStorage],
  );

  useEffect(() => {
    const initializeLanguage = async () => {
      let lang: string | null = getLocalStorage({ key: "language" });

      if (!lang) {
        const browserLang = navigator.language;
        lang = browserLang.startsWith("es")
          ? "es"
          : browserLang.startsWith("pt")
            ? "pt"
            : browserLang.startsWith("en")
              ? "en"
              : "es";
      }
      if (lang !== i18n.language) {
        await setLanguage(lang);
      }
    };

    initializeLanguage();
  }, []);

  const translation = useCallback(
    <K extends keyof Translations[N]>(key: K, options?: TOptions): Translations[N][K] => {
      return t(key as string, options) as Translations[N][K];
    },
    [t],
  );

  return {
    t: translation,
    setLanguage,
    activeLanguage,
  };
};

export default useTranslations;
